import React, {useEffect} from "react";
import {useDispatch, useSelector} from "react-redux";
import {RootState, AppDispatch} from "../store/store";
import {setImageQuality, setLanguage, setNotifications, setAutoplayTrailers} from "../store/settingsSlice";
import {useTranslation} from "react-i18next";

interface SettingsModalProps {
  onClose: () => void;
}

const LANGUAGES = [
  {code: "en", label: "English", flag: "🇬🇧"},
  {code: "es", label: "Español", flag: "🇪🇸"},
  {code: "fr", label: "Français", flag: "🇫🇷"},
  {code: "ru", label: "Русский", flag: "🇷🇺"},
];

const QUALITIES = ["480p", "720p", "1080p", "4K"] as const;

interface ToggleProps {
  checked: boolean;
  onChange: () => void;
}
const Toggle: React.FC<ToggleProps> = ({checked, onChange}) => (
  <label className="relative inline-block w-[42px] h-[24px] cursor-pointer flex-shrink-0">
    <input type="checkbox" checked={checked} onChange={onChange} className="opacity-0 w-0 h-0 peer" />
    <span className="absolute inset-0 bg-raised2 border border-border2 rounded-full transition-all peer-checked:bg-accent peer-checked:border-accent before:content-[''] before:absolute before:w-[16px] before:h-[16px] before:left-[3px] before:top-[3px] before:bg-white before:rounded-full before:transition-all peer-checked:before:translate-x-[18px]" />
  </label>
);

export const SettingsModal: React.FC<SettingsModalProps> = ({onClose}) => {
  const dispatch: AppDispatch = useDispatch();
  const {imageQuality, language, notifications, autoplayTrailers} = useSelector(
    (state: RootState) => state.settings
  );
  const {i18n} = useTranslation();

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  const handleLanguageChange = (code: string) => {
    i18n.changeLanguage(code);
    localStorage.setItem("language", code);
    dispatch(setLanguage(code));
  };

  return (
    <div className="fixed inset-0 z-[600] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      {/* Panel */}
      <div className="relative z-10 w-full max-w-[440px] bg-surface border border-border2 rounded-lg shadow-lg overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-base font-bold text-t1">Settings</h2>
          <button
            className="w-7 h-7 flex items-center justify-center rounded-xs bg-raised2 text-t2 hover:text-t1 border-none transition-colors duration-fast"
            onClick={onClose}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="px-6 py-5 flex flex-col gap-6">
          {/* Language */}
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.6px] text-t3 mb-3">Language</p>
            <div className="grid grid-cols-2 gap-2">
              {LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => handleLanguageChange(lang.code)}
                  className={`flex items-center gap-2.5 px-3 py-2.5 rounded-sm border text-left text-[13px] font-medium transition-all duration-fast cursor-pointer ${
                    (language || i18n.language) === lang.code
                      ? "bg-accent-muted border-accent text-accent"
                      : "bg-raised border-border text-t2 hover:border-border2 hover:text-t1"
                  }`}
                >
                  <span className="text-base">{lang.flag}</span>
                  {lang.label}
                </button>
              ))}
            </div>
          </div>

          {/* Image Quality */}
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.6px] text-t3 mb-3">Image Quality</p>
            <div className="flex gap-2">
              {QUALITIES.map((q) => (
                <button
                  key={q}
                  onClick={() => dispatch(setImageQuality(q))}
                  className={`flex-1 py-2 rounded-xs border text-[12px] font-semibold transition-all duration-fast cursor-pointer ${
                    imageQuality === q
                      ? "bg-accent-muted border-accent text-accent"
                      : "bg-raised border-border text-t2 hover:border-border2 hover:text-t1"
                  }`}
                >
                  {q}
                </button>
              ))}
            </div>
          </div>

          {/* Toggles */}
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[13px] font-medium text-t1">Autoplay Trailers</p>
                <p className="text-[11px] text-t3 mt-0.5">Auto-play trailers on content pages</p>
              </div>
              <Toggle
                checked={autoplayTrailers}
                onChange={() => dispatch(setAutoplayTrailers(!autoplayTrailers))}
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[13px] font-medium text-t1">Notifications</p>
                <p className="text-[11px] text-t3 mt-0.5">Receive update notifications</p>
              </div>
              <Toggle
                checked={notifications}
                onChange={() => dispatch(setNotifications(!notifications))}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
